import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WeekDateRangeResponse } from '../../api/generated';
import type { GanttGroup, GanttSeries, TaggedInterventionGroup } from './types';
import { GanttCell } from './GanttCell';
import type { MoveHoverParams } from './GanttCell';
import { packSeriesIntoRows } from '../../utils/ganttUtils';
import { isWeekPast } from '../../utils/weekClassifier';
import { resolveCellColors } from '../../utils/cellColorResolver';
import { PatientWarningModal } from './PatientWarningModal';
import type { PlanPatient } from './planPatients';
import styles from './GanttGroupRow.module.css';

export interface SeriesSlot {
  seriesIndex: number;
  type: 'ivBlock' | 'connector' | 'empty';
  groups: TaggedInterventionGroup[];
  connectLeft: boolean;
  connectRight: boolean;
  strokeColor: string;
  fillColor: string;
  highlightColor: string;
  textColor: string | undefined;
  seriesMinWeek: number;
  seriesMaxWeek: number;
  treatmentPlanId?: string;
  originYear?: number;
  // The plan crosses the year boundary here: 'out' on the last week of a plan
  // that runs on into next year, 'in' on the first week of a carried-over plan.
  yearEdge?: 'in' | 'out';
}

interface SeriesWeekData {
  weekMap: Map<number, TaggedInterventionGroup[]>;
  range: { min: number; max: number };
}

function buildSeriesWeekData(series: GanttSeries): SeriesWeekData {
  const weekMap = new Map<number, TaggedInterventionGroup[]>();
  let min = Infinity;
  let max = -Infinity;

  for (const g of series.groups) {
    const weekNumber = g.weekNumber;
    const tagged: TaggedInterventionGroup = {
      ...g,
      treatmentPlanId: series.treatmentPlanId,
      seriesIndex: series.planIndex,
      seriesLabel: series.seriesLabel,
      strokeColor: series.strokeColor,
      fillColor: series.fillColor,
      highlightColor: series.highlightColor
    };
    const existing = weekMap.get(weekNumber);
    if (existing) {
      existing.push(tagged);
    } else {
      weekMap.set(weekNumber, [tagged]);
    }
    if (weekNumber < min) min = weekNumber;
    if (weekNumber > max) max = weekNumber;
  }

  return { weekMap, range: { min, max } };
}

function groupSeriesByVisualRow(
  series: GanttSeries[],
  visualRowMap: ReadonlyMap<number, number>
): Map<number, GanttSeries[]> {
  const result = new Map<number, GanttSeries[]>();
  for (const s of series) {
    const vr = visualRowMap.get(s.planIndex) ?? 0;
    const existing = result.get(vr);
    if (existing) existing.push(s);
    else result.set(vr, [s]);
  }
  return result;
}

function collectSeriesHighlightIds(
  groups: TaggedInterventionGroup[],
  params: MoveHoverParams
): Set<string> {
  const ids = new Set<string>();
  for (const g of groups) {
    if (g.treatmentPlanId === params.treatmentPlanId && g.id) ids.add(g.id);
  }
  return ids;
}

interface GanttGroupRowProps {
  group: GanttGroup;
  totalWeeks: number;
  weeks: WeekDateRangeResponse[];
  isEditMode?: boolean;
  readOnly?: boolean;
  onGroupClick?: (treatmentPlanId: string, groupId: string) => void;
  onMoveIntervention?: (params: MoveHoverParams) => void;
  onMoveStart?: () => void;
  onMoveDiscard?: () => void;
  onMoveAccept?: () => void;
  onAddTreatmentPlanBatch?: (treatmentPlanTemplateId: string, weekNumber: number, mode: 'single' | 'series') => void;
  // Asks the chart to raise the removal dialog for one plan. Optional like every
  // other edit handler here, because TemplateGanttPreview renders this row
  // read-only and passes none of them: an unwired row offers no removal at all
  // rather than an inert dialog.
  onRequestRemoval?: (treatmentPlanId: string) => void;
  // Real bookings per plan, derived once by the chart. Read here to warn before
  // a move takes a patient's booking with it.
  patientsByPlanId?: ReadonlyMap<string, PlanPatient[]>;
  onAddPatient?: (treatmentPlanId: string, groupId: string) => void;
}

// Shared empty default, so a read-only row does not rebuild one per render.
const NO_PLAN_PATIENTS: ReadonlyMap<string, PlanPatient[]> = new Map();

export const GanttGroupRow = ({
  group,
  totalWeeks,
  weeks,
  isEditMode,
  readOnly,
  onGroupClick,
  onMoveIntervention,
  onMoveStart,
  onMoveDiscard,
  onMoveAccept,
  onAddTreatmentPlanBatch,
  onRequestRemoval,
  patientsByPlanId = NO_PLAN_PATIENTS,
  onAddPatient
}: GanttGroupRowProps) => {
  const { t } = useTranslation();
  const visualRowMap = useMemo(() => packSeriesIntoRows(group.series), [group.series]);
  const visualRowCount = useMemo(() => {
    let max = -1;
    for (const row of visualRowMap.values()) {
      if (row > max) max = row;
    }
    return max + 1;
  }, [visualRowMap]);
  const seriesCount = visualRowCount || group.series.length;
  const [highlightedIds, setHighlightedIds] = useState<ReadonlySet<string>>(new Set());
  const [movingGroupId, setMovingGroupId] = useState<string | null>(null);
  // Acknowledged move warnings are per edit session. The row is keyed on edit
  // mode by its parent and so remounts, which is what scopes this to the session.
  const [plansWithAcknowledgedWarning, setPlansWithAcknowledgedWarning] = useState<Set<string>>(new Set());
  const [pendingMove, setPendingMove] = useState<{
    groupId: string;
    treatmentPlanId: string;
  } | null>(null);

  const pendingMovePatients = useMemo(
    () => (pendingMove ? patientsByPlanId.get(pendingMove.treatmentPlanId) ?? [] : []).map((p) => p.name),
    [pendingMove, patientsByPlanId]
  );

  useEffect(() => {
    if (!movingGroupId) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-move-wrapper]')) {
        setMovingGroupId(null);
        setHighlightedIds(new Set());
        onMoveDiscard?.();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [movingGroupId, onMoveDiscard]);

  // Build per-series, per-week group map
  const seriesWeekMap: Map<number, Map<number, TaggedInterventionGroup[]>> = new Map();
  const seriesWeekRanges: Map<number, { min: number; max: number }> = new Map();

  for (const series of group.series) {
    const { weekMap, range } = buildSeriesWeekData(series);
    seriesWeekMap.set(series.planIndex, weekMap);
    seriesWeekRanges.set(series.planIndex, range);
  }

  // Group series by visual row
  const seriesByVisualRow = groupSeriesByVisualRow(group.series, visualRowMap);

  const today = useMemo(() => new Date(), []);
  const rawSlots: Omit<SeriesSlot, 'connectLeft' | 'connectRight'>[][] = Array.from(
    { length: totalWeeks },
    (_, i) => {
      const week = i + 1;
      const past = isWeekPast(week, weeks, today);
      return Array.from({ length: seriesCount }, (__, vr) => {
        const rowSeries = seriesByVisualRow.get(vr) ?? [];

        for (const series of rowSeries) {
          const weekMap = seriesWeekMap.get(series.planIndex);
          const range = seriesWeekRanges.get(series.planIndex);
          const groupsInWeek = weekMap?.get(week);
          const seriesMinWeek = range?.min ?? 0;
          const seriesMaxWeek = range?.max ?? 0;
          const patientCount = series.groups
            .flatMap((g) => g.assignedPatients ?? [])
            .filter((p) => !p.shadow).length;
          const colors = resolveCellColors(
            series.fillColor,
            series.strokeColor,
            past,
            patientCount
          );

          if (groupsInWeek && groupsInWeek.length > 0) {
            return {
              seriesIndex: vr,
              type: 'ivBlock' as const,
              groups: groupsInWeek,
              strokeColor: colors.strokeColor,
              fillColor: colors.fillColor,
              highlightColor: series.highlightColor,
              textColor: colors.textColor,
              seriesMinWeek,
              seriesMaxWeek,
              treatmentPlanId: series.treatmentPlanId,
              originYear: series.originYear
            };
          }

          if (range && week > range.min && week < range.max) {
            return {
              seriesIndex: vr,
              type: 'connector' as const,
              groups: [],
              strokeColor: colors.strokeColor,
              fillColor: colors.fillColor,
              highlightColor: series.highlightColor,
              textColor: colors.textColor,
              seriesMinWeek,
              seriesMaxWeek,
              treatmentPlanId: series.treatmentPlanId,
              originYear: series.originYear
            };
          }
        }

        const fallback = rowSeries[0];
        return {
          seriesIndex: vr,
          type: 'empty' as const,
          groups: [],
          strokeColor: fallback?.strokeColor ?? '',
          fillColor: fallback?.fillColor ?? '',
          highlightColor: fallback?.highlightColor ?? '',
          textColor: undefined,
          seriesMinWeek: 0,
          seriesMaxWeek: 0
        };
      });
    }
  );

  const weekSlots: SeriesSlot[][] = rawSlots.map((slots, weekIdx) =>
    slots.map((slot, seriesIdx) => {
      const left = weekIdx > 0 ? rawSlots[weekIdx - 1][seriesIdx] : undefined;
      const right = weekIdx < totalWeeks - 1 ? rawSlots[weekIdx + 1][seriesIdx] : undefined;
      const samePlanLeft =
        left != null &&
        slot.treatmentPlanId != null &&
        left.treatmentPlanId === slot.treatmentPlanId;
      const samePlanRight =
        right != null &&
        slot.treatmentPlanId != null &&
        right.treatmentPlanId === slot.treatmentPlanId;
      let yearEdge: SeriesSlot['yearEdge'];
      if (slot.type !== 'empty' && weekIdx === totalWeeks - 1 && slot.seriesMaxWeek > totalWeeks) {
        yearEdge = 'out';
      } else if (slot.originYear !== undefined && weekIdx + 1 === slot.seriesMinWeek) {
        yearEdge = 'in';
      }
      return {
        ...slot,
        connectLeft: samePlanLeft && (left.type === 'ivBlock' || left.type === 'connector'),
        connectRight: samePlanRight && (right.type === 'ivBlock' || right.type === 'connector'),
        yearEdge
      };
    })
  );

  const weekColumns = weekSlots.map((slots, weekIndex) => ({
    weekNumber: weekIndex + 1,
    slots
  }));

  const allTaggedGroups = useMemo(() => {
    const result: TaggedInterventionGroup[] = [];
    for (const slots of weekSlots) {
      for (const slot of slots) {
        if (slot.type === 'ivBlock') {
          result.push(...slot.groups);
        }
      }
    }
    return result;
  }, [weekSlots]);

  const handleMoveHover = useCallback(
    (params: MoveHoverParams | null) => {
      if (!params) {
        setHighlightedIds(new Set());
        return;
      }

      if (params.scope === 'single') {
        setHighlightedIds(new Set([params.groupId]));
        return;
      }

      setHighlightedIds(collectSeriesHighlightIds(allTaggedGroups, params));
    },
    [allTaggedGroups]
  );

  return (
    <div className={styles.row}>
      {weekColumns.map(({ weekNumber, slots }) => (
        <GanttCell
          key={weekNumber}
          slots={slots}
          seriesCount={seriesCount}
          isFirst={weekNumber === 1}
          isLast={weekNumber === totalWeeks}
          isEditMode={isEditMode}
          readOnly={readOnly}
          onGroupClick={onGroupClick}
          highlightedIds={highlightedIds}
          onMoveHover={handleMoveHover}
          onMoveIntervention={onMoveIntervention}
          totalWeeks={totalWeeks}
          onAddTreatmentPlanBatch={onAddTreatmentPlanBatch ? (mode: 'single' | 'series') => onAddTreatmentPlanBatch(group.templateId, weekNumber, mode) : undefined}
          onAddPatient={onAddPatient}
          movingGroupId={movingGroupId}
          onDeleteTreatmentPlan={onRequestRemoval}
          onSetMovingGroupId={(id, treatmentPlanId) => {
            if (id !== null && treatmentPlanId) {
              const planPatients = patientsByPlanId.get(treatmentPlanId) ?? [];
              if (planPatients.length > 0 && !plansWithAcknowledgedWarning.has(treatmentPlanId)) {
                setPendingMove({ groupId: id, treatmentPlanId });
                return;
              }
              onMoveStart?.();
            } else {
              onMoveAccept?.();
              setHighlightedIds(new Set());
            }
            setMovingGroupId(id);
          }}
        />
      ))}
      {pendingMove && (
        <PatientWarningModal
          title={t('move-warning-title')}
          message={t('move-warning-message')}
          patientsLabel={t('move-warning-patients-label')}
          confirmLabel={t('move-warning-continue')}
          cancelLabel={t('move-warning-cancel')}
          patientNames={pendingMovePatients}
          onClose={() => setPendingMove(null)}
          onConfirm={() => {
            setPlansWithAcknowledgedWarning((prev) => new Set(prev).add(pendingMove.treatmentPlanId));
            onMoveStart?.();
            setMovingGroupId(pendingMove.groupId);
            setPendingMove(null);
          }}
        />
      )}
    </div>
  );
};
